import { clsx, type ClassValue } from 'clsx'

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHour < 24) return `${diffHour}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return formatDate(d)
}

export function highlightKeywords(
  text: string,
  keywords: { keyword: string; case_sensitive: boolean; is_regex: boolean; color: string }[]
): { html: string; matches: string[] } {
  const matches: string[] = []
  let result = text

  for (const kw of keywords) {
    try {
      let regex: RegExp
      if (kw.is_regex) {
        regex = new RegExp(`(${kw.keyword})`, kw.case_sensitive ? 'g' : 'gi')
      } else {
        const escaped = kw.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        regex = new RegExp(`(${escaped})`, kw.case_sensitive ? 'g' : 'gi')
      }

      const before = result
      result = result.replace(
        regex,
        `<mark style="background-color: ${kw.color}; padding: 0 2px; border-radius: 2px;">$1</mark>`
      )

      if (result !== before) {
        matches.push(kw.keyword)
      }
    } catch {
      // Invalid regex, skip
    }
  }

  return { html: result, matches }
}

export function matchesKeywords(
  text: string,
  keywords: { keyword: string; case_sensitive: boolean; is_regex: boolean }[]
): string[] {
  const matches: string[] = []

  for (const kw of keywords) {
    try {
      let regex: RegExp
      if (kw.is_regex) {
        regex = new RegExp(kw.keyword, kw.case_sensitive ? '' : 'i')
      } else {
        const escaped = kw.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        regex = new RegExp(escaped, kw.case_sensitive ? '' : 'i')
      }

      if (regex.test(text)) {
        matches.push(kw.keyword)
      }
    } catch {
      // Invalid regex, skip
    }
  }

  return matches
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length - 3) + '...'
}
